import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connectRefinementList } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as ChevronDown } from '../../../assets/images/svg/chevron-down.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import BottomDrawer from '../../Drawer/Drawer';
import Button from '../../Button/Button';

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: 6px;
  background: ${themeColors.colorWhite};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorPrimary};
  cursor: pointer;
  width: fit-content;

  > svg {
    width: 14px;
    height: 14px;
  }
`;

const ChangeAffordance = styled.span`
  color: ${themeColors.colorLabel};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;

// Scoped drawer title (Instrument Sans 600/14px/100% per Figma); passed as a
// node so the shared Drawer header stays unchanged for other drawers.
const DrawerTitle = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const AllOption = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid
    ${({ $active }) =>
      $active ? themeColors.colorPrimary : themeColors.colorBorderPrimary};
  background: ${({ $active }) =>
    $active ? themeColors.colorBackgroundSecondary : themeColors.colorWhite};
  cursor: pointer;
  text-align: left;
  margin-bottom: 12px;
`;

const RadioOuter = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${themeColors.colorPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $active }) =>
      $active ? themeColors.colorPrimary : 'transparent'};
  }
`;

const AllText = styled.div`
  display: flex;
  flex-direction: column;
`;

const AllTitle = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextPrimary};
`;

const AllSubtitle = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: ${themeColors.colorTextSecondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const WeightCheckbox = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $checked }) =>
      $checked ? themeColors.colorTextSecondary : 'transparent'};
  background: ${themeColors.colorBackgroundSecondary};
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextPrimary};
  text-align: left;
`;

const CheckBox = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid
    ${({ $checked }) =>
      $checked ? themeColors.colorTextSecondary : themeColors.colorTextPrimary};
  background: ${({ $checked }) =>
    $checked ? themeColors.colorTextSecondary : 'transparent'};
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
    position: absolute;
    left: 4px;
    top: 0;
    width: 5px;
    height: 10px;
    border: solid ${themeColors.colorWhite};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const FooterWrap = styled.div`
  display: flex;

  > button {
    width: 100%;
  }
`;

// Drop facet values whose label/value contains a comma — those are malformed
// index entries (a single product with WeightCaption stored as a comma-joined
// list of weights, which Algolia exposes as one facet value).
const isCleanWeight = (item) =>
  !String(item?.label ?? '').includes(',') &&
  !String(item?.value ?? '').includes(',');

const SellFromHomeWeightDropdown = ({
  items: rawItems,
  currentRefinement,
  refine,
}) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [draft, setDraft] = useState([]);

  // The connector's `currentRefinement` is the canonical list of applied
  // refinement labels. Use it directly rather than deriving from
  // `items[*].isRefined`, since Algolia's facet response can omit items
  // depending on disjunctive-facet config.
  const refinedLabels = currentRefinement || [];

  // Algolia's facet response only contains weights reachable under the
  // current refinement set, so once weights are applied the unselected
  // options drop out of `rawItems`. Remember every clean label we have
  // ever seen and union it with the current items + currently-refined
  // labels so the drawer keeps showing the full list.
  const knownLabelsRef = useRef([]);
  const cleanCurrent = rawItems.filter(isCleanWeight);
  [...cleanCurrent.map((i) => i.label), ...refinedLabels].forEach((label) => {
    if (!knownLabelsRef.current.includes(label)) {
      knownLabelsRef.current.push(label);
    }
  });
  const items = knownLabelsRef.current.map((label) => ({
    label,
    isRefined: refinedLabels.includes(label),
  }));

  const triggerLabel = (() => {
    if (refinedLabels.length === 0) {
      return t('sellWizard.sfhWizard.weightAll');
    }
    if (refinedLabels.length === 1) {
      return refinedLabels[0];
    }
    return `${refinedLabels.length} selected`;
  })();

  useEffect(() => {
    if (isDrawerOpen) {
      setDraft(refinedLabels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  const toggleDraftLabel = (label) => {
    setDraft((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const handleApply = () => {
    refine(draft);
    setIsDrawerOpen(false);
  };

  const isAllActive = draft.length === 0;

  const drawerFooter = (
    <FooterWrap>
      <Button
        variant="primaryOutlined"
        textTransform="none"
        onClick={handleApply}
      >
        {t('sellWizard.sfhWizard.weightDrawer.apply')}
      </Button>
    </FooterWrap>
  );

  return (
    <>
      <Trigger type="button" onClick={() => setIsDrawerOpen(true)}>
        {triggerLabel}{' '}
        <ChangeAffordance>
          {t('sellWizard.sfhWizard.weightChange')}
        </ChangeAffordance>
        <ChevronDown />
      </Trigger>
      <BottomDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={
          <DrawerTitle>
            {t('sellWizard.sfhWizard.weightDrawer.title')}
          </DrawerTitle>
        }
        footer={drawerFooter}
        maxHeight="80%"
      >
        <AllOption
          type="button"
          $active={isAllActive}
          onClick={() => setDraft([])}
        >
          <RadioOuter $active={isAllActive} />
          <AllText>
            <AllTitle>
              {t('sellWizard.sfhWizard.weightDrawer.allWeightsTitle')}
            </AllTitle>
            <AllSubtitle>
              {t('sellWizard.sfhWizard.weightDrawer.allWeightsSubtitle')}
            </AllSubtitle>
          </AllText>
        </AllOption>
        <Grid>
          {items.map((item) => {
            const isChecked = draft.includes(item.label);
            return (
              <WeightCheckbox
                key={item.label}
                type="button"
                $checked={isChecked}
                onClick={() => toggleDraftLabel(item.label)}
              >
                <CheckBox $checked={isChecked} />
                {item.label}
              </WeightCheckbox>
            );
          })}
        </Grid>
      </BottomDrawer>
    </>
  );
};

SellFromHomeWeightDropdown.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentRefinement: PropTypes.arrayOf(PropTypes.string),
  refine: PropTypes.func.isRequired,
};

export default connectRefinementList(SellFromHomeWeightDropdown);
